import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { format } from "date-fns";
import { toast } from "sonner";
import { Bell, Send, Mail, Smartphone, Users, Clock, CheckCircle, Trash2, Search } from "lucide-react";
import { groupPlayersForPicker, filterGroupedPlayers } from "../lib/playerGrouping";
import useScrollRestoration from "../hooks/useScrollRestoration";

const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [players, setPlayers] = useState([]);
  const [sections, setSections] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter-86: preserve scroll position on detail navigation
  useScrollRestoration({ ready: !loading });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [audienceType, setAudienceType] = useState("all"); // "all" | "event" | "selected"
  const [selectedEventId, setSelectedEventId] = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    notification_type: "email"
  });

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();
  // iter-95: open-notification popup (email-style modal view)
  const [openNotif, setOpenNotif] = useState(null);

  // iter-112: the picker / audience UI must NEVER include deactivated
  // players. Backend ``GET /players`` returns inactive rows too (so the
  // admin Players page can show them for management), but on the Send
  // Notification dialog we only want recipients who can actually receive
  // mail. Define ``active players`` exactly the same way the reminder
  // sender does (``is_active && player_type in {member, dep}``) so the
  // count, the picker, and the actual send all agree.
  const activePlayers = players.filter(
    (p) => p.is_active !== false && ["member", "dep"].includes(p.player_type),
  );

  useEffect(() => {
    fetchData();
    // Refetch whenever the admin-mode scope flips so admins see the right
    // history list without a hard reload.
  }, [showAdminControls]);

  const fetchData = async () => {
    try {
      // iter-95: admins in Admin Mode see ALL band notifications. Members
      // and admins-in-Member-View see only their own inbox.
      const scope = showAdminControls ? "all" : "inbox";
      const [notificationsRes, playersRes, eventsRes, sectionsRes] = await Promise.all([
        apiClient.get(`/notifications?scope=${scope}`),
        apiClient.get("/players").catch(() => ({ data: [] })),
        apiClient.get("/events").catch(() => ({ data: [] })),
        apiClient.get("/sections").catch(() => ({ data: [] })),
      ]);
      setNotifications(notificationsRes.data);
      setPlayers((playersRes.data || []).filter(p => p.is_active));
      setSections(sectionsRes.data || []);
      const nowIso = new Date().toISOString();
      setUpcomingEvents(
        (eventsRes.data || [])
          .filter((e) => (e.start_time || "") >= nowIso)
          .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
          .slice(0, 50)
      );
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // iter-95: open a notification in a popup and mark it read for the current
  // user. We update the local list immediately for a snappy UI, then fire the
  // API call in the background.
  const openNotificationPopup = async (notification) => {
    setOpenNotif(notification);
    if (!notification?.is_read) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, is_read: true } : n
        )
      );
      try {
        await apiClient.post(`/notifications/${notification.id}/read`);
      } catch {
        // If the API call fails, rollback the optimistic update
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: false } : n
          )
        );
      }
    }
  };

  // 80-character preview, single line, strips newlines
  const previewText = (msg) => {
    const flat = (msg || "").replace(/\s+/g, " ").trim();
    return flat.length > 80 ? flat.slice(0, 80) + "…" : flat;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let recipients;
    if (audienceType === "all") {
      recipients = ["all"];
    } else if (audienceType === "event") {
      if (!selectedEventId) {
        toast.error("Please select an event");
        return;
      }
      recipients = [`event:${selectedEventId}`];
    } else {
      if (selectedPlayers.length === 0) {
        toast.error("Please select at least one recipient");
        return;
      }
      recipients = selectedPlayers.filter((p) => p !== "all");
    }

    setSending(true);
    try {
      await apiClient.post("/notifications/send", {
        ...formData,
        recipients
      });
      
      toast.success("Notification sent successfully!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      notification_type: "email"
    });
    setSelectedPlayers([]);
    setAudienceType("all");
    setSelectedEventId("");
  };

  const togglePlayer = (playerId) => {
    if (playerId === "all") {
      if (selectedPlayers.includes("all")) {
        setSelectedPlayers([]);
      } else {
        setSelectedPlayers(["all"]);
      }
    } else {
      if (selectedPlayers.includes("all")) {
        setSelectedPlayers([playerId]);
      } else if (selectedPlayers.includes(playerId)) {
        setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
      } else {
        setSelectedPlayers([...selectedPlayers, playerId]);
      }
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm("Delete this notification from history? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success("Notification deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete notification");
    }
  };

  const handleClearAllNotifications = async () => {
    if (notifications.length === 0) return;
    if (!window.confirm(`Delete ALL ${notifications.length} notification(s) from history? This cannot be undone.`)) return;
    try {
      const res = await apiClient.delete("/notifications");
      setNotifications([]);
      toast.success(res.data?.message || "Notifications cleared");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear notifications");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Notifications</h1>
          <p className="text-muted-foreground mt-1">
            {showAdminControls
              ? "Send notifications to band members"
              : "Your inbox — announcements and reminders from the band"}
          </p>
        </div>
        {showAdminControls && (
          <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            className="border-red-500/40 text-red-300 hover:bg-red-500/10"
            onClick={async () => {
              if (!window.confirm("Strip dep recipients from every previously-sent in-app notification? Already-delivered emails + push pings can't be recalled.")) return;
              try {
                const res = await apiClient.post("/notifications/cleanup-dep-recipients");
                toast.success(res.data?.message || "Cleanup done");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Cleanup failed");
              }
            }}
            data-testid="cleanup-dep-recipients-btn"
            title="Removes dep user_ids from every notification's recipient list so deps stop seeing them in their inbox"
          >
            Unsend to deps
          </Button>
          <Button
            variant="outline"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            onClick={async () => {
              if (!window.confirm("Send a polite 'please disregard' apology email to every dep in this band who hasn't yet been notified? Each dep gets it once only.")) return;
              try {
                const res = await apiClient.post("/notifications/disregard-notice-to-deps");
                toast.success(res.data?.message || "Disregard notice sent");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Send failed");
              }
            }}
            data-testid="disregard-notice-deps-btn"
            title="Sends a follow-up apology email to every dep, asking them to ignore the previous band-wide notifications"
          >
            Send disregard notice
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground btn-glow" data-testid="send-notification-btn">
                <Send className="w-4 h-4 mr-2" />
                Send Notification
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-white/10 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Send Notification</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2 mb-2">
                Recipients will see this attributed to <span className="text-foreground/80 font-medium">{(user?.name || "you").trim()}</span> — replies go to your email.
              </p>
              <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
                <div className="space-y-4 flex-1 overflow-y-auto pr-2">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      data-testid="notification-title-input"
                      placeholder="e.g., Rehearsal Reminder"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-secondary/50"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      data-testid="notification-message-input"
                      placeholder="Enter your message..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="bg-secondary/50 min-h-[100px]"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notification Type</Label>
                    <Select 
                      value={formData.notification_type || "email"} 
                      onValueChange={(value) => setFormData({ ...formData, notification_type: value })}
                    >
                      <SelectTrigger className="bg-secondary/50" data-testid="notification-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Email Only
                          </div>
                        </SelectItem>
                        <SelectItem value="push">
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4" />
                            Push Only
                          </div>
                        </SelectItem>
                        <SelectItem value="both">
                          <div className="flex items-center gap-2">
                            <Bell className="w-4 h-4" />
                            Email & Push
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Audience</Label>
                    <Select value={audienceType} onValueChange={(v) => { setAudienceType(v); setSelectedPlayers([]); setSelectedEventId(""); }}>
                      <SelectTrigger className="bg-secondary/50" data-testid="audience-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          <div className="flex items-center gap-2"><Users className="w-4 h-4" /> All active players</div>
                        </SelectItem>
                        <SelectItem value="event">
                          <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> Everyone attending an event</div>
                        </SelectItem>
                        <SelectItem value="selected">
                          <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Choose specific players</div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {audienceType === "event" && (
                    <div className="space-y-2" data-testid="event-audience-block">
                      <Label>Event</Label>
                      <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                        <SelectTrigger className="bg-secondary/50" data-testid="event-select">
                          <SelectValue placeholder={upcomingEvents.length === 0 ? "No upcoming events" : "Select an event"} />
                        </SelectTrigger>
                        <SelectContent>
                          {upcomingEvents.map((ev) => (
                            <SelectItem key={ev.id} value={ev.id}>
                              {ev.title} — {new Date(ev.start_time).toLocaleDateString()} {new Date(ev.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Sends to every active player <strong>except</strong> those who&apos;ve RSVP&apos;d &ldquo;Not attending&rdquo;.
                      </p>
                    </div>
                  )}

                  {audienceType === "selected" && (
                  <div className="space-y-2">
                    <Label>Recipients</Label>
                    <div className="border border-white/10 rounded-lg p-4 bg-secondary/20">
                      {/* Select All */}
                      <div className="flex items-center gap-3 pb-3 border-b border-white/5 mb-3">
                        <Checkbox
                          id="select-all"
                          checked={selectedPlayers.includes("all")}
                          onCheckedChange={() => togglePlayer("all")}
                          data-testid="select-all-checkbox"
                        />
                        <Label htmlFor="select-all" className="flex items-center gap-2 cursor-pointer">
                          <Users className="w-4 h-4 text-primary" />
                          <span className="font-medium">All Players ({activePlayers.length})</span>
                        </Label>
                      </div>
                      
                      {/* Search filter — iter-66l */}
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          value={recipientSearch}
                          onChange={(e) => setRecipientSearch(e.target.value)}
                          placeholder="Search by name, email or section…"
                          className="pl-9 h-8 text-sm"
                          data-testid="notification-recipient-search"
                        />
                      </div>

                      {/* Player List — grouped by member/dep + section (iter-66l) */}
                      <ScrollArea className="h-64">
                        <div className="space-y-3 pr-2">
                          {filterGroupedPlayers(
                            groupPlayersForPicker(activePlayers, sections),
                            recipientSearch
                          ).map((group) => (
                            <div key={group.key} data-testid={`recipients-group-${group.key}`}>
                              <div className="flex items-center justify-between sticky top-0 bg-card/95 backdrop-blur-sm py-1 mb-1.5 z-10">
                                <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                                  {group.label}
                                </span>
                                <span className="text-[10px] text-muted-foreground tabular-nums">
                                  {group.rows.length}
                                </span>
                              </div>
                              <div className="space-y-1.5 pl-1">
                                {group.rows.map((player) => (
                                  <div key={player.id} className="flex items-center gap-3">
                                    <Checkbox
                                      id={`player-${player.id}`}
                                      checked={selectedPlayers.includes("all") || selectedPlayers.includes(player.id)}
                                      onCheckedChange={() => togglePlayer(player.id)}
                                      disabled={selectedPlayers.includes("all")}
                                    />
                                    <Label
                                      htmlFor={`player-${player.id}`}
                                      className="flex-1 cursor-pointer text-sm"
                                    >
                                      {player.name}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      
                      <p className="text-xs text-muted-foreground mt-3">
                        {selectedPlayers.includes("all") 
                          ? `Sending to all ${players.length} players`
                          : `${selectedPlayers.length} player(s) selected`
                        }
                      </p>
                    </div>
                  </div>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-white/5 mt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-primary text-primary-foreground" 
                    disabled={sending}
                    data-testid="confirm-send-btn"
                  >
                    {sending ? "Sending..." : "Send Notification"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Stats — compact single row, admin-mode only */}
      {showAdminControls && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="glass-card rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3" data-testid="stat-total-sent">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">Total</p>
              <p className="text-lg sm:text-xl font-heading font-bold leading-tight">{notifications.length}</p>
            </div>
          </div>
          <div className="glass-card rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3" data-testid="stat-email">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-blue-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">Email</p>
              <p className="text-lg sm:text-xl font-heading font-bold leading-tight">
                {notifications.filter(n => n.notification_type === "email" || n.notification_type === "both").length}
              </p>
            </div>
          </div>
          <div className="glass-card rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3" data-testid="stat-push">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-4 h-4 text-green-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">Push</p>
              <p className="text-lg sm:text-xl font-heading font-bold leading-tight">
                {notifications.filter(n => n.notification_type === "push" || n.notification_type === "both").length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notification History / Inbox */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="font-heading text-lg">
              {showAdminControls ? "Notification History" : "Inbox"}
            </CardTitle>
            {showAdminControls && notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllNotifications}
                className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                data-testid="clear-all-notifications-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4">
          {notifications.length > 0 ? (
            <div className="space-y-2">
              {notifications.map((notification, index) => {
                const isUnread = !notification.is_read;
                const senderName =
                  notification.created_by_name ||
                  (notification.created_by === user?.id ? user?.name : null) ||
                  "Band admin";
                const initial = (senderName || "?").trim().charAt(0).toUpperCase();
                return (
                  <button
                    type="button"
                    key={notification.id}
                    onClick={() => openNotificationPopup(notification)}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-lg transition-colors animate-slide-in border ${
                      isUnread
                        ? "bg-primary/[0.06] border-primary/20 hover:bg-primary/[0.10]"
                        : "bg-secondary/30 border-transparent hover:bg-secondary/50"
                    }`}
                    style={{ animationDelay: `${index * 0.04}s` }}
                    data-testid={`notification-row-${notification.id}`}
                  >
                    {/* Avatar with sender's initial */}
                    <div className="relative w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                      {initial}
                      {isUnread && (
                        <span
                          aria-hidden
                          className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-background"
                          data-testid={`notification-unread-dot-${notification.id}`}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* From + time */}
                      <div className="flex items-baseline justify-between gap-2">
                        <p className={`text-sm truncate ${isUnread ? "font-semibold text-foreground" : "text-foreground/90"}`}>
                          {senderName}
                        </p>
                        <span className="text-[11px] text-muted-foreground flex-shrink-0">
                          {format(new Date(notification.sent_at), "d MMM · h:mm a")}
                        </span>
                      </div>
                      {/* Title */}
                      <p className={`text-sm truncate ${isUnread ? "text-foreground" : "text-muted-foreground"}`}>
                        {notification.title}
                      </p>
                      {/* 80-char preview */}
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {previewText(notification.message)}
                      </p>
                    </div>
                    {showAdminControls && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                        className="text-red-400 hover:bg-red-500/10 hover:text-red-300 flex-shrink-0 h-8 w-8"
                        data-testid={`delete-notification-${notification.id}`}
                        aria-label="Delete notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>
                {showAdminControls
                  ? "No notifications sent yet"
                  : "Nothing in your inbox yet"}
              </p>
              {showAdminControls && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setDialogOpen(true)}
                >
                  Send Your First Notification
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* iter-95: email-style popup — full title, sender attribution, full message */}
      <Dialog open={!!openNotif} onOpenChange={(o) => { if (!o) setOpenNotif(null); }}>
        <DialogContent className="glass border-white/10 max-w-2xl max-h-[85vh] flex flex-col" data-testid="notification-popup">
          {openNotif && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl pr-6 break-words">
                  {openNotif.title}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-3 pb-3 border-b border-white/5">
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {((openNotif.created_by_name || user?.name || "?").trim().charAt(0).toUpperCase())}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" data-testid="notification-popup-sender">
                    {openNotif.created_by_name ||
                      (openNotif.created_by === user?.id ? user?.name : null) ||
                      "Band admin"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(openNotif.sent_at), "EEE d MMM yyyy 'at' h:mm a")}
                  </p>
                </div>
                <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">
                  {openNotif.notification_type}
                </Badge>
              </div>
              <div
                className="flex-1 overflow-y-auto pr-2 -mr-2 mt-3 text-sm text-foreground/90 whitespace-pre-wrap break-words"
                data-testid="notification-popup-body"
              >
                {openNotif.message}
              </div>
              <div className="flex justify-end pt-3 border-t border-white/5">
                <Button variant="ghost" onClick={() => setOpenNotif(null)}>Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
